function initProductPage(container = document) {
  const cleanupPanels = () => {
    container.querySelectorAll('.pp-accordion__panel li > p').forEach((p) => {
      const li = p.parentNode;
      while (p.firstChild) li.insertBefore(p.firstChild, p);
      p.remove();
    });

    container.querySelectorAll('.pp-accordion__panel td > p, .pp-accordion__panel th > p').forEach((p) => {
      const cell = p.parentNode;
      while (p.firstChild) cell.insertBefore(p.firstChild, p);
      p.remove();
    });

    container.querySelectorAll('.pp-accordion__panel p').forEach((p) => {
      const text = p.textContent.replace(/\u00A0/g, '').trim();
      if (!text && p.children.length === 0) {
        p.remove();
      }
    });
  };

  /* =========================
     Carousel
     ========================= */
  const galleryRoot = container.querySelector('[data-pp-gallery]');
  if (galleryRoot) {
    const slides = Array.from(galleryRoot.querySelectorAll('[data-pp-slide]'));
    const prev = galleryRoot.querySelector('[data-pp-prev]');
    const next = galleryRoot.querySelector('[data-pp-next]');
    const thumbs = Array.from(galleryRoot.querySelectorAll('[data-pp-dot]'));

    let index = 0;

    const show = (i) => {
      if (!slides.length) return;

      index = (i + slides.length) % slides.length;

      slides.forEach((slide, idx) => {
        const active = idx === index;
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      thumbs.forEach((thumb, idx) => {
        const active = idx === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        show(parseInt(thumb.dataset.index, 10) || 0);
      });
    });

    show(0);
  }

  /* =========================
     Qty controls
     ========================= */
  const minus = container.querySelector('[data-pp-qty-minus]');
  const plus = container.querySelector('[data-pp-qty-plus]');
  const input = container.querySelector('[data-pp-qty-input]');

  const clamp = (n) => Math.max(1, n);

  minus?.addEventListener('click', () => {
    if (!input) return;
    input.value = clamp((parseInt(input.value, 10) || 1) - 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  plus?.addEventListener('click', () => {
    if (!input) return;
    input.value = clamp((parseInt(input.value, 10) || 1) + 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* =========================
     AJAX add to cart + drawer event
     ========================= */
  const form = container.querySelector('#pp-product-form');
  const atcButton = form?.querySelector('[type="submit"][name="add"]');

  if (form && !form.dataset.ppBound) {
    form.dataset.ppBound = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!atcButton || atcButton.disabled) return;

      const formData = new FormData(form);
      const quantity = parseInt(formData.get('quantity'), 10) || 1;
      const originalText = atcButton.textContent;

      atcButton.disabled = true;
      atcButton.classList.remove('is-added', 'is-error');
      atcButton.textContent = 'Adding...';

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: formData
        });

        if (!addRes.ok) {
          let message = 'Unable to add this item to cart.';
          try {
            const errorData = await addRes.json();
            if (errorData?.description) message = errorData.description;
          } catch (e) {
            // ignore JSON parse failure
          }
          throw new Error(message);
        }

        const addedItem = await addRes.json();

        atcButton.textContent = 'Added';
        atcButton.classList.add('is-added');

        animateCartFeedback(atcButton, quantity);

        window.dispatchEvent(
          new CustomEvent('cart:added', {
            detail: { item: addedItem, quantity }
          })
        );

        window.dispatchEvent(
          new CustomEvent('cart:refresh', {
            detail: {
              open: true,
              source: 'product-page',
              item: addedItem,
              quantity
            }
          })
        );
      } catch (error) {
        console.error(error);
        atcButton.textContent = 'Unavailable';
        atcButton.classList.add('is-error');

        window.dispatchEvent(
          new CustomEvent('cart:error', {
            detail: { message: error.message }
          })
        );
      } finally {
        window.setTimeout(() => {
          atcButton.disabled = false;
          atcButton.textContent = originalText;
          atcButton.classList.remove('is-added', 'is-error');
        }, 1400);
      }
    });
  }

  /* =========================
     Accordion: auto-split description
     ========================= */
  const accordionRoot = container.querySelector('[data-pp-accordion]');
  if (!accordionRoot) return;

  const itemsWrap = accordionRoot.querySelector('[data-pp-accordion-items]');
  const source = accordionRoot.querySelector('[data-pp-accordion-source]');

  if (!itemsWrap || !source) return;

  const sourceHTML = (source.innerHTML || '').trim();
  if (!sourceHTML) return;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = sourceHTML;

  const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const headingToSection = (text) => {
    const t = normalize(text).replace(/:$/, '');

    if (t.includes('feature')) return 'Features';
    if (t.includes('spec')) return 'Specifications';
    if (t === 'description' || t.includes('product description')) return 'Description';
    if (t.includes('shipping')) return 'Shipping';
    if (t.includes('warranty')) return 'Warranty';
    if (t.includes('return')) return 'Returns';

    return null;
  };

  const nodes = Array.from(wrapper.childNodes);
  const sections = [];
  let current = { title: 'Description', nodes: [] };

  const pushCurrent = () => {
    const html = current.nodes
      .map((node) => node.outerHTML ?? node.textContent ?? '')
      .join('')
      .trim();

    if (html) {
      sections.push({ title: current.title, html });
    }
  };

  nodes.forEach((node) => {
    if (node.nodeType === 1) {
      const tag = node.tagName.toUpperCase();

      if (/^H[1-6]$/.test(tag)) {
        const newTitle = headingToSection(node.textContent);
        if (newTitle) {
          pushCurrent();
          current = { title: newTitle, nodes: [] };
          return;
        }
      }

      if (tag === 'P') {
        const onlyStrong =
          node.children.length === 1 &&
          node.children[0].tagName?.toUpperCase() === 'STRONG' &&
          normalize(node.textContent) === normalize(node.children[0].textContent);

        if (onlyStrong) {
          const newTitle = headingToSection(node.textContent);
          if (newTitle) {
            pushCurrent();
            current = { title: newTitle, nodes: [] };
            return;
          }
        }
      }
    }

    current.nodes.push(node);
  });

  pushCurrent();

  if (sections.length <= 1) {
    itemsWrap.innerHTML = `
      <div class="pp-accordion__item is-open">
        <button
          type="button"
          class="pp-accordion__trigger"
          aria-expanded="true"
          aria-controls="pp-accordion-panel-0"
          id="pp-accordion-trigger-0"
        >
          <span class="pp-accordion__title">Description</span>
          <span class="pp-accordion__icon" aria-hidden="true">+</span>
        </button>
        <div
          class="pp-accordion__panel rte"
          id="pp-accordion-panel-0"
          role="region"
          aria-labelledby="pp-accordion-trigger-0"
        >
          <div class="pp-accordion__content">
            ${sourceHTML}
          </div>
        </div>
      </div>
    `;
    source.hidden = true;
    cleanupPanels();
    return;
  }

  const unique = [];
  const seen = new Set();

  for (const section of sections) {
    const key = normalize(section.title);

    if (seen.has(key)) {
      const found = unique.find((entry) => normalize(entry.title) === key);
      if (found) found.html += section.html;
    } else {
      seen.add(key);
      unique.push(section);
    }
  }

  const makeId = (title, i) =>
    `pp-accordion-${normalize(title).replace(/[^a-z0-9]+/g, '-')}-${i}`;

  itemsWrap.innerHTML = '';

  unique.forEach((section, i) => {
    const id = makeId(section.title, i);
    const triggerId = `${id}-trigger`;
    const panelId = `${id}-panel`;
    const isOpen = i === 0;

    const item = document.createElement('div');
    item.className = `pp-accordion__item${isOpen ? ' is-open' : ''}`;

    item.innerHTML = `
      <button
        type="button"
        class="pp-accordion__trigger"
        id="${triggerId}"
        aria-expanded="${isOpen ? 'true' : 'false'}"
        aria-controls="${panelId}"
      >
        <span class="pp-accordion__title">${section.title}</span>
        <span class="pp-accordion__icon" aria-hidden="true">+</span>
      </button>

      <div
        class="pp-accordion__panel rte"
        id="${panelId}"
        role="region"
        aria-labelledby="${triggerId}"
        ${isOpen ? '' : 'hidden'}
      >
        <div class="pp-accordion__content">
          ${section.html}
        </div>
      </div>
    `;

    itemsWrap.appendChild(item);
  });

  const items = Array.from(itemsWrap.querySelectorAll('.pp-accordion__item'));

  items.forEach((item) => {
    const trigger = item.querySelector('.pp-accordion__trigger');
    const panel = item.querySelector('.pp-accordion__panel');

    if (!trigger || !panel) return;

    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      panel.hidden = isOpen;
      item.classList.toggle('is-open', !isOpen);
    });
  });

  source.hidden = true;
  cleanupPanels();
}

/* =========================
   Cart feedback animation
   ========================= */
function animateCartFeedback(button, quantity = 1) {
  button.classList.add('pp-atc--pulse');
  window.setTimeout(() => {
    button.classList.remove('pp-atc--pulse');
  }, 550);

  const cartTarget =
    document.querySelector('[data-open-cart]') ||
    document.querySelector('.hdr__cartBtn');

  if (!cartTarget) return;

  cartTarget.classList.add('cart-bump');
  window.setTimeout(() => {
    cartTarget.classList.remove('cart-bump');
  }, 700);

  let badge = cartTarget.querySelector('.pp-cart-added-badge');

  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'pp-cart-added-badge';
    cartTarget.appendChild(badge);
  }

  badge.textContent = `+${quantity}`;
  badge.classList.remove('is-animating');
  void badge.offsetWidth;
  badge.classList.add('is-animating');
}

document.addEventListener('DOMContentLoaded', () => {
  initProductPage(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initProductPage(event.target);
});